import React from 'react';
import { Clock, Layers, AlertCircle, CheckCircle, DollarSign, Search, Shield, ChevronRight, Zap, Target, Smartphone, HeartHandshake, Home, Briefcase } from 'lucide-react';

export const ProblemSection: React.FC = () => {
  return (
    <section id="problem" className="py-40 px-6 bg-[#0d0d0d] border-y border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-12">
            <div>
              <h2 className="text-xs uppercase tracking-[0.4em] text-red-500 font-bold mb-6">The High Cost of Silence</h2>
              <h3 className="text-5xl md:text-8xl font-serif italic leading-[1] text-white tracking-tighter">
                The cost of not listening is higher than ever.
              </h3>
            </div>
            
            <p className="text-white text-3xl leading-relaxed font-bold">
              66% of people feel unheard by their government and leaders.
            </p>

            <div className="p-8 border-l-4 border-red-500 bg-red-500/5 space-y-8">
              <p className="text-3xl font-serif text-white leading-snug font-bold">
                When engagement breaks down, leaders are forced to react instead of lead: costing time, money, and public trust.
              </p>
              <p className="text-4xl font-bold text-white tracking-tight">
                Campaigns spend thousands to reach voters, only to lose visibility once elected.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: <Clock size={24} />, text: "Slow and expensive" },
                { icon: <Layers size={24} />, text: "Difficult to scale" },
                { icon: <AlertCircle size={24} />, text: "Fragmented systems" }
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-4 text-xl text-white font-black group">
                  <div className="text-red-500 transition-transform group-hover:scale-110">{item.icon}</div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="glass-island p-12 rounded-[3rem] relative z-10 shadow-2xl space-y-12">
              <h4 className="text-[10px] uppercase tracking-[0.3em] font-black text-center text-white/70">Resource Efficiency Index</h4>
              
              <div className="space-y-10">
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <span className="text-xs uppercase tracking-widest text-red-500 font-black">Traditional Engagement</span>
                    <span className="text-xl font-bold text-white">$10 / Engagement</span>
                  </div>
                  <div className="h-4 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-full bg-red-500"></div>
                  </div>
                  <p className="text-[12px] text-white/50 uppercase tracking-widest font-bold">Takes weeks or months for data</p>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <span className="text-xs uppercase tracking-widest text-brand-blue font-black">Boulai Platform</span>
                    <span className="text-xl font-bold text-white">&lt; $0.50 / Engagement</span>
                  </div>
                  <div className="h-4 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-1/12 bg-brand-blue"></div>
                  </div>
                  <p className="text-[12px] text-brand-blue uppercase tracking-widest font-black">Immediate Analysis: Thousands engaged instantly</p>
                </div>
              </div>
              
              <div className="pt-8 text-center border-t border-white/10">
                <p className="text-lg text-white font-bold italic">Shift from reactive recovery to proactive leadership.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const OpportunitySection: React.FC = () => {
  return (
    <section id="opportunity" className="py-40 px-6 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-24 items-center">
        <div className="order-2 lg:order-1 relative">
           <div className="grid grid-cols-2 gap-6 relative z-10">
              {[
                { icon: <CheckCircle className="text-brand-blue" />, label: "Minutes, not months" },
                { icon: <DollarSign className="text-green-500" />, label: "Reduce Polling Spend" },
                { icon: <Search className="text-purple-500" />, label: "Stay Visible Daily" },
                { icon: <Shield className="text-blue-400" />, label: "Verified Inputs" },
                { icon: <HeartHandshake className="text-red-400" />, label: "Face your citizens' problems" },
                { icon: <Smartphone className="text-yellow-400" />, label: "Engage where they want" }
              ].map((item, i) => (
                <div key={i} className={`glass-island p-8 rounded-2xl flex flex-col items-center justify-center space-y-4 text-center hover:bg-white/5 transition-all group ${i % 2 !== 0 ? 'mt-8' : ''}`}>
                   <div className="group-hover:scale-125 transition-transform duration-500">{item.icon}</div>
                   <p className="text-[10px] uppercase tracking-widest font-black text-white">{item.label}</p>
                </div>
              ))}
           </div>
           
           <div className="mt-12 glass-island p-8 rounded-3xl border-brand-blue/20">
              <div className="flex items-center gap-6 text-white">
                <div className="flex -space-x-3">
                   <div className="w-10 h-10 rounded-full bg-brand-blue/20 border-2 border-brand-black flex items-center justify-center"><Home size={16}/></div>
                   <div className="w-10 h-10 rounded-full bg-brand-blue/40 border-2 border-brand-black flex items-center justify-center"><Briefcase size={16}/></div>
                   <div className="w-10 h-10 rounded-full bg-brand-blue border-2 border-brand-black flex items-center justify-center"><Smartphone size={16}/></div>
                </div>
                <p className="text-xs uppercase tracking-widest font-black">Engage from home, work, or mobile: whenever, wherever.</p>
              </div>
           </div>
        </div>

        <div className="order-1 lg:order-2 space-y-10">
          <div>
            <h2 className="text-xs uppercase tracking-[0.4em] text-brand-blue font-bold mb-6">The Opportunity</h2>
            <h3 className="text-5xl md:text-8xl font-serif italic leading-[1] tracking-tighter mb-8 text-white">
              Engage directly. <br /> Decide faster. <br /> Build trust.
            </h3>
          </div>
          
          <p className="text-white text-3xl leading-relaxed font-bold">
            Boulai makes it fast, simple, and secure to gather input, analyze it with AI, and make decisions your community supports.
          </p>

          <ul className="space-y-8">
            {[
              "Collect feedback in minutes instead of months",
              "Face your citizens' problems by helping them how, when, and how they need it",
              "Make it easier for governments and citizens to engage who and where they want",
              "Stay in front of your constituents outside traditional media",
              "Make decisions backed by real, verified public input"
            ].map((bullet, i) => (
              <li key={i} className="flex items-start space-x-4 group">
                <div className="mt-1 flex-shrink-0 w-8 h-8 bg-brand-blue/20 rounded-full flex items-center justify-center transition-colors group-hover:bg-brand-blue">
                  <CheckCircle size={18} className="text-brand-blue group-hover:text-white" />
                </div>
                <span className="text-2xl text-white font-bold transition-colors">{bullet}</span>
              </li>
            ))}
          </ul>
          
          <div className="pt-8 space-y-6">
            <a 
              href="#platform" 
              className="inline-flex items-center space-x-3 text-brand-blue font-black text-sm uppercase tracking-[0.3em] hover:text-white transition-all group"
            >
              <span>Explore Platform</span>
              <ChevronRight size={18} className="group-hover:translate-x-2 transition-transform" />
            </a>
            <div className="block">
              <a 
                href="#policynow" 
                className="inline-flex items-center space-x-3 text-white/50 font-black text-[10px] uppercase tracking-[0.4em] hover:text-brand-blue transition-all group"
              >
                <span>See how we farm engagement from your supporter base</span>
                <ChevronRight size={14} className="group-hover:translate-x-2 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};